import React from "react";
import { useTranslation } from "react-i18next";

import { Chip, Stack, Tooltip } from "@mui/material";
import { Box } from "@mui/system";
import { DataGrid, GridColDef, GridLoadingOverlay } from "@mui/x-data-grid";

import { ColoredChip } from "@/components/EnumChip";
import {
  CompanyCount,
  CompanySummary,
  useUsersSafeSendersQuery,
} from "@/pages/banners/remote-apis/bannerUsersApi";

export function SafeMarkCompaniesCell({
  companies,
}: {
  companies: CompanyCount[];
}) {
  if (!companies.length) return <>-</>;

  const sortedCompanies = [...companies].sort((a, b) => b.count - a.count);

  const visibleCompanies = sortedCompanies.slice(0, 5);
  const hiddenCompanies = sortedCompanies.slice(5);

  const getChipColor = (count: number) => {
    if (count >= 10) return "warning";
    if (count >= 5) return "info";
    return "neutral.dark";
  };

  return (
    <Stack direction="row" flexWrap="wrap" gap="1rem">
      {visibleCompanies.map((c) => (
        <Tooltip key={c.company} title={`${c.company}: ${c.count}`}>
          <ColoredChip
            label={`${c.company}: ${c.count}`}
            size="small"
            chipColor={getChipColor(c.count)}
            sx={{
              maxWidth: 200,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          />
        </Tooltip>
      ))}

      {hiddenCompanies.length > 0 && (
        <Tooltip
          title={hiddenCompanies
            .map((c) => `${c.company}: ${c.count}`)
            .join(", ")}
        >
          <Chip
            label={`+${hiddenCompanies.length} more`}
            size="small"
            variant="outlined"
          />
        </Tooltip>
      )}
    </Stack>
  );
}

export const UserSafeSenders = ({
  companies,
}: {
  companies: CompanySummary[];
}) => {
  const { t } = useTranslation();

  const {
    data = [],
    isLoading,
    isFetching,
  } = useUsersSafeSendersQuery({
    companies: companies.map((c) => c.company_key_domain),
  });

  const rowsToDisplay = data;

  const columns: GridColDef<(typeof data)[number]>[] = [
    {
      field: "email_address",
      headerName: t("Email Address"),
      width: 400,
    },
    {
      field: "total_count",
      headerName: t("Total Count"),
      width: 150,
    },
    {
      field: "safe_mark_count_by_company",
      headerName: t("Companies"),
      width: 800,
      renderCell: (params) => (
        <SafeMarkCompaniesCell
          companies={params.row.safe_mark_count_by_company || []}
        />
      ),
    },
  ];

  return (
    <Box sx={{ height: 600, width: "100%", position: "relative" }}>
      <DataGrid
        columns={columns}
        rows={rowsToDisplay}
        getRowId={(row) => row.email_address}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 20,
            },
          },
        }}
        pageSizeOptions={[10, 20, 50, 100]}
        checkboxSelection={!isLoading}
        loading={isFetching || isLoading}
        disableRowSelectionOnClick
        slots={{
          loadingOverlay: GridLoadingOverlay,
        }}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "skeleton",
          },
        }}
      />
    </Box>
  );
};
